import { onboardingFeatures } from '@/features';
import { eventBus, ONBOARDING_DIALOG_CLOSED_EVENT } from '@/events';

/**
 * Módulo encargado gestionar los mensajes de bienvenida del CMS. Sus responsabilidades son:
 *  - saber si se debe mostrar el diálogo con las novedades de bCube cuando un usuario accede al CMS,
 *    - en tal caso, saber qué novedades deben ser mostradas (solo aquellas que el usuario todavía no haya visto,
 *      agrupadas en bloques de 6)
 *  - mostrar todas las novedades de bCube si el usuario pulsa en el botón correspondiente de la barra de herramientas.
 *
 * Este módulo debe iniciarse después de obtener los datos del usuario haya iniciado sesión, para poder saber qué
 * novedades ya han sido mostradas.
 * @see FETCH_PROFILE_DATA
 */
export default {
  namespaced: true,
  state: {
    initialized: false,
    dialogShown: false,
    showAllOnboardingFeatures: false,
  },
  getters: {
    lastFeaturedViewed: (state, getters, rootState) => rootState.profile.onboarding.lastFeaturedViewed,
    onboardingFeatures: (state, getters, rootState) => {
      const lastFeatureViewed = state.showAllOnboardingFeatures ? 0 : getters.lastFeaturedViewed;
      const limit = state.showAllOnboardingFeatures ? onboardingFeatures.length : 6;
      return onboardingFeatures
        .filter(feature => feature.id > lastFeatureViewed)
        .filter(feature => (feature.requiredFeature ? rootState.config.features[feature.requiredFeature] : true))
        .slice(0, limit);
    },
    showDialog: (state, getters) => getters.onboardingFeatures.length > 0
      && state.initialized
      && (!state.dialogShown || state.showAllOnboardingFeatures),
  },
  mutations: {
    SET_INITIALIZED(state, dialogShouldBeDisplayed) {
      state.initialized = true;
      state.dialogShown = !dialogShouldBeDisplayed;
    },
    SHOW_ALL_ONBOARDING_FEATURES(state) {
      state.showAllOnboardingFeatures = true;
    },
    CLOSE_DIALOG(state) {
      state.dialogShown = true;
      state.showAllOnboardingFeatures = false;
    },
  },
  actions: {
    /**
     * Inicia el módulo, calculando si hay novedades que el usuario no ha visto todavía y, en tal caso, mostrando el
     * diálogo con las novedades que todavía no se han mostrado.
     * Si no hay novedades pendientes, se emite directamente el evento `ONBOARDING_DIALOG_CLOSED_EVENT`.
     * @see ONBOARDING_DIALOG_CLOSED_EVENT
     */
    INITIALIZE_ONBOARDING_MANAGER({
      commit, getters, state, rootState,
    }) {
      if (state.initialized) {
        return;
      }

      const dialogShouldBeDisplayed = onboardingFeatures
        .some(feature => feature.id > getters.lastFeaturedViewed
          && (feature.requiredFeature ? rootState.config.features[feature.requiredFeature] : true));

      if (!dialogShouldBeDisplayed) {
        eventBus.$emit(ONBOARDING_DIALOG_CLOSED_EVENT);
      }

      commit('SET_INITIALIZED', dialogShouldBeDisplayed);
    },

    /**
     * Cierra el diálogo con las novedades de bCube.
     * Si se está cerrando el diálogo inicial, además se emite el evento `ONBOARDING_DIALOG_CLOSED_EVENT`, y se
     * actualiza la última novedad visualizada por el usuario en su perfil, para que la próxima vez que inicie sesión
     * no le aparezcan repetidas.
     * @see ONBOARDING_DIALOG_CLOSED_EVENT
     */
    async CLOSE_DIALOG({
      commit, dispatch, getters, state,
    }) {
      if (!state.initialized
        || !getters.showDialog
      ) {
        return;
      }

      if (!state.dialogShown) {
        eventBus.$emit(ONBOARDING_DIALOG_CLOSED_EVENT);
        dispatch('_UPDATE_ONBOARDING_LAST_FEATURE_VIEWED');
      }

      commit('CLOSE_DIALOG');
    },

    /**
     * Muestra el diálogo con todas las novedades de bCube.
     * Esta acción se lanza desde el menú del usuario en la barra de herramientas general.
     */
    SHOW_ALL_ONBOARDING_FEATURES({ state, commit }) {
      if (!state.initialized) return;
      commit('SHOW_ALL_ONBOARDING_FEATURES');
    },

    /**
     * Actualiza la última novedad vista por el usuario en su perfil.
     * @private
     */
    async _UPDATE_ONBOARDING_LAST_FEATURE_VIEWED({ dispatch, getters }) {
      if (getters.onboardingFeatures.length === 0) {
        return;
      }

      const lastFeatureShown = getters.onboardingFeatures[getters.onboardingFeatures.length - 1].id;
      await dispatch('UPDATE_ONBOARDING_LAST_FEATURE_VIEWED', lastFeatureShown, { root: true });
    },
  },
};
